import React from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import {
  CLIENT_SELF_PAY_STATUS,
  SELF_PAY_ZENDESK_ARTICLE_URL,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ProgramCoverageBreakdown } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { HPISelfPayProviderBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/hpiSelfPayProviderBanner/HPISelfPayProviderBanner'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { openUrl } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'

import { healthPlanStates } from '../../../../../common/constants/appConstants'
import { isHPIAndSelfPaySupported } from '../../../../../common/utils/utils'

const InlineText = styled(BodyText)`
  display: contents;
`

const successFormatter = (color: string) => (chunks: string) => (
  <InlineText bold color={color} size={BodyTextSize.DEFAULT} text={chunks} />
)

const linkFormatter = (color: string) => (chunks: string) => {
  return (
    <Link
      onPress={() => openUrl(SELF_PAY_ZENDESK_ARTICLE_URL)}
      size={BodyTextSize.DEFAULT}
      style={{ textDecorationLine: 'underline', color }}
      text={chunks}
    />
  )
}

export type HPISelfPayStatus = {
  isActive: boolean
  inlineMessage: React.ReactNode | undefined
  banner: React.ReactNode | undefined
}

// Used to display a banner or inline message that overrides useHealthStatus and useSelfPayContinuationOfCareStatus if the
// client is HPI only and self-pay eligible.
export const useHPISelfPayStatus = (
  clientDetails: ClientObject | undefined,
  programName: string | undefined,
  programCoverageBreakdown: ProgramCoverageBreakdown | undefined,
): HPISelfPayStatus => {
  const { isSelfPayEnabled } = useFlags()
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const isActive = isHPIAndSelfPaySupported({
    clientDetails,
    programName,
    programCoverageBreakdown,
    isSelfPayEnabled,
  })

  if (!isActive || !clientDetails) {
    return { isActive: false, inlineMessage: undefined, banner: undefined }
  }

  const clientFirstName = clientDetails.preferred_first_name || clientDetails.first_name || ''
  const customerName = clientDetails.displayed_employer ?? '-'
  const healthPlanStatus = clientDetails.health_plan_status
  const isSelfPayActive = clientDetails.self_pay_status === CLIENT_SELF_PAY_STATUS.ACTIVE
  const isHealthPlanIneligibleOrMissing =
    healthPlanStatus === healthPlanStates.NOT_READY || healthPlanStatus === healthPlanStates.NOT_ELIGIBLE

  const success = successFormatter(colors.textSuccess)
  const link = linkFormatter(colors.textSecondary)

  let inlineMessage: React.ReactNode | undefined
  if (healthPlanStatus === healthPlanStates.READY) {
    inlineMessage = formatMessage(
      {
        defaultMessage: '{clientFirstName} <success>is ready to use their health plan for their sessions.</success>',
        description:
          'Provider message when HPI-supported self-pay-eligible client has an eligible health plan with cost share payment method on file',
      },
      { clientFirstName, success },
    )
  } else if (isHealthPlanIneligibleOrMissing && isSelfPayActive) {
    inlineMessage = formatMessage(
      {
        defaultMessage:
          '{clientFirstName} <success>is ready to use self-pay for their sessions.</success> {customerName} offers a self-pay option for clients to pay for sessions themselves. <link>Learn more about self-pay here.</link>',
        description:
          'Provider message when HPI-supported self-pay-eligible client does not have an eligible health plan but has self-pay set up',
      },
      { clientFirstName, customerName, success, link },
    )
  }

  let banner: React.ReactNode | undefined
  if (healthPlanStatus === healthPlanStates.HEALTH_PLAN_PAYMENT_MISSING) {
    banner = (
      <HPISelfPayProviderBanner
        variant='paymentMissing'
        clientFirstName={clientFirstName}
        customerName={customerName}
      />
    )
  } else if (isHealthPlanIneligibleOrMissing && !isSelfPayActive) {
    banner = (
      <HPISelfPayProviderBanner
        variant='needsInfoOrEnrollment'
        clientFirstName={clientFirstName}
        customerName={customerName}
      />
    )
  }

  return { isActive, inlineMessage, banner }
}
